/**
 * P-43 (D-213..D-215) — "שיוך ללקוח": pure rules of the button and its dialog (no DOM, no React).
 */

/** A person of the chosen customer, as GET /tickets/:id/assign-customer/candidates returns it. */
export interface AssignCandidate {
  kind: 'user' | 'contact';
  id: string;
  user_id: string | null;
  contact_id: string | null;
  name: string;
  email: string | null;
  phone: string | null;
  match: boolean;
}

/** The refusals the server names with `error_code`, each with an i18n key under `ticketDetail.assignCustomer.errors`. */
export const ASSIGN_ERROR_CODES = [
  'ASSIGN_TASK', 'ASSIGN_ALREADY_LINKED', 'ASSIGN_NOT_UNLINKED', 'ASSIGN_TARGET_INTERNAL', 'ASSIGN_TARGET_INACTIVE',
  'ASSIGN_HAS_TASK_SUBTICKETS', 'ASSIGN_TIMER_RUNNING', 'ASSIGN_HOURS_LOCKED', 'ASSIGN_HOURS_BILLED',
] as const;
export type AssignErrorCode = typeof ASSIGN_ERROR_CODES[number];

/** The code of a refused assignment, or null for any other error (shown with the server text). */
export function assignErrorCode(err: unknown): AssignErrorCode | null {
  const e = err as { error?: { error_code?: string } } | null;
  const code = e?.error?.error_code;
  return (ASSIGN_ERROR_CODES as readonly string[]).includes(code ?? '') ? (code as AssignErrorCode) : null;
}

/** The button shows ONLY on a ticket the server calls "without a customer", for staff, not cancelled. */
export function showAssignCustomer(ticket: { is_unlinked?: boolean; task_type?: string; cancelled_at?: string | null }, isStaff: boolean): boolean {
  return isStaff && ticket.is_unlinked === true && ticket.task_type !== 'task' && !ticket.cancelled_at;
}

/** The best guess for "opened by": the person whose e-mail is the sender's (never forced). */
export function suggestedCandidate(list: AssignCandidate[]): AssignCandidate | null {
  return list.find((c) => c.match) ?? null;
}

/** The request body: the customer, and the person only when one was chosen (QX-162: never required). */
export function buildAssignBody(organizationId: string, chosen: AssignCandidate | null): { organization_id: string; customer_id?: string; contact_id?: string } {
  const body: { organization_id: string; customer_id?: string; contact_id?: string } = { organization_id: organizationId };
  if (chosen?.user_id) body.customer_id = chosen.user_id;
  if (chosen?.contact_id) body.contact_id = chosen.contact_id;
  return body;
}

/** Stable key of a candidate in the picker. */
export function candidateKey(c: AssignCandidate): string {
  return `${c.kind}:${c.id}`;
}
